import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useInspectionTemplates, useInspections, useCreateInspection } from '@/hooks/useInspections';
import { useSites } from '@/hooks/useSites';
import { ClipboardCheck, Building2, MapPin, Play, Clock, FileText } from 'lucide-react';
import { format } from 'date-fns';

interface NewInspectionProps {
  onStartInspection: (inspectionId: string) => void;
  onResumeInspection: (inspectionId: string) => void;
}

export const NewInspection: React.FC<NewInspectionProps> = ({ 
  onStartInspection,
  onResumeInspection 
}) => {
  const { data: templates = [], isLoading: templatesLoading } = useInspectionTemplates();
  const { sites = [], isLoading: sitesLoading } = useSites();
  const { data: draftInspections = [] } = useInspections({ status: 'draft' });
  const createInspection = useCreateInspection();

  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [area, setArea] = useState('');

  const activeTemplates = templates.filter(t => t.is_active);

  const handleStartInspection = async () => {
    if (!selectedTemplate) return;

    const inspection = await createInspection.mutateAsync({
      template_id: selectedTemplate,
      site_id: selectedSite && selectedSite !== 'none' ? selectedSite : undefined,
      area: area || undefined
    });

    onStartInspection(inspection.id);
  };

  if (templatesLoading || sitesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Draft Inspections */}
      {draftInspections.length > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-warning" />
              Resume Draft Inspections
            </CardTitle>
            <CardDescription>
              You have {draftInspections.length} inspection(s) in progress
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {draftInspections.map((inspection) => (
                <div 
                  key={inspection.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{inspection.template?.name || 'Unnamed Template'}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {inspection.site && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {inspection.site.name}
                          </span>
                        )}
                        {inspection.area && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {inspection.area}
                          </span>
                        )}
                        <span>Started {format(new Date(inspection.started_at), 'MMM d, h:mm a')}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => onResumeInspection(inspection.id)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Resume
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Start New Inspection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Start New Inspection
          </CardTitle>
          <CardDescription>
            Select a template and location to begin a new inspection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {activeTemplates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No inspection templates available.</p>
              <p className="text-sm">Contact an administrator to create templates.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="template">Inspection Template *</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger id="template">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="h-4 w-4" />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="site">Facility / Site</Label>
                <Select value={selectedSite} onValueChange={setSelectedSite}>
                  <SelectTrigger id="site">
                    <SelectValue placeholder="Select a facility (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific facility</SelectItem>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {site.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="area">Area / Location</Label>
                <Input
                  id="area"
                  placeholder="e.g., Building A, Floor 2, Room 101..."
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Specify the specific area or unit being inspected
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full"
                disabled={!selectedTemplate || createInspection.isPending}
                onClick={handleStartInspection}
              >
                <Play className="h-5 w-5 mr-2" />
                {createInspection.isPending ? 'Starting...' : 'Start Inspection'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Template Quick Info */}
      {selectedTemplate && (
        <Card className="bg-muted/30">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ClipboardCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">
                  {activeTemplates.find(t => t.id === selectedTemplate)?.name}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {activeTemplates.find(t => t.id === selectedTemplate)?.description || 
                   'No description available'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
